import { useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { createPublicClaimApi } from '@/consumer/lib/public-claim-api'
import { type DisclaimerConfig } from '@/shared/components/claims/disclaimer'

export type HostType = 'VEHICLE' | 'PROPERTY' | 'VESSEL' | ''

interface ConfigResponse {
  host_type: HostType
  /** Tenant-authored disclaimers keyed by context (e.g. claim_submit). */
  disclaimers?: Record<string, DisclaimerConfig>
}

interface ClaimFieldMeta {
  code_name: string
  display_name: string
  field_type: string
  required: boolean
  configuration_data: Record<string, unknown>
}

export interface LookupCandidate {
  holding_uuid: string
  product_name: string
  display_identity: string
}

export interface LookupResponse {
  holding_uuid?: string
  product_name?: string
  display_identity?: string
  host_type: HostType
  fields?: ClaimFieldMeta[]
  candidates?: LookupCandidate[]
}

interface SubmitResponse {
  uuid: string
  reference: string
  message: string
}

interface FileUploadResponse {
  uuid: string
  file_url: string
  file_name: string
}

export function usePublicClaimConfig(tenantSlug: string) {
  const api = useMemo(() => createPublicClaimApi(tenantSlug), [tenantSlug])

  return useQuery<ConfigResponse>({
    queryKey: ['public-claim-config', tenantSlug],
    queryFn: async () => {
      const res = await api.get('/config/')
      return res.data
    },
    staleTime: Infinity,
    enabled: !!tenantSlug,
  })
}

export function usePublicClaimLookup(tenantSlug: string) {
  const api = useMemo(() => createPublicClaimApi(tenantSlug), [tenantSlug])

  return useMutation<LookupResponse, unknown, { query: string; holding_uuid?: string }>({
    mutationFn: async (payload) => {
      const res = await api.post('/lookup/', payload)
      return res.data
    },
  })
}

export function usePublicClaimFileUpload(tenantSlug: string) {
  const api = useMemo(() => createPublicClaimApi(tenantSlug), [tenantSlug])

  return useMutation<
    FileUploadResponse,
    Error,
    { file: File; use_case: string }
  >({
    mutationFn: ({ file, use_case }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('use_case', use_case)
      return api
        .post('/files/upload/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data)
    },
  })
}

export function usePublicClaimSubmit(tenantSlug: string) {
  const api = useMemo(() => createPublicClaimApi(tenantSlug), [tenantSlug])

  return useMutation<
    SubmitResponse,
    unknown,
    { holding_uuid: string; captured_values: Record<string, unknown> }
  >({
    mutationFn: async (payload) => {
      const res = await api.post('/submit/', payload)
      return res.data
    },
  })
}
